import { ServiceType, ConditionI } from "@/types/referals";

export const serviceTypeToUtm: Record<ServiceType, string> = {
  delivery: "utm_DeliveryRefMiniApp",
  taxi: "utm_TaxiRefMiniApp",
  eats: "utm_EatsRefMiniApp",
};

export function onlyNumbers(value: string, allowedChars = ""): string {
  const reg = new RegExp(`[^0-9${allowedChars}]`, "g");
  return value.trim().replace(reg, "");
}

export const parsDate = (startDate: string, endDate: string) => {
  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    timeZone: "Europe/Moscow",
  };
  const startDay = new Date(startDate).toLocaleString("ru-RU", {
    ...dateFormatOptions,
    day: "numeric",
  });
  const endDay = new Date(endDate).toLocaleString("ru-RU", {
    ...dateFormatOptions,
    day: "numeric",
  });
  const month = new Date(endDate).toLocaleString("ru-RU", {
    ...dateFormatOptions,
    month: "long",
  });
  const result = `с ${startDay} по ${endDay} ${month}`;

  return result;
};

export const fullName = (
  firstName: string | null | undefined,
  middleName: string | null | undefined,
  lastName: string | null | undefined
) => {
  const isLastName = lastName ?? "";
  const firstInitial = firstName ? `${firstName.charAt(0)}.` : "";
  const middleInitial = middleName ? `${middleName.charAt(0)}.` : "";
  const formattedName = `${isLastName} ${firstInitial} ${middleInitial}`.trim();

  return formattedName;
};

export const serviceTypeLabel = (value: ServiceType): string => {
  switch (value) {
    case "delivery":
      return "Доставка";
    case "taxi":
      return "Такси";
    case "eats":
      return "Еда";
    default:
      return "";
  }
};

/** Формы слова для русских числительных: [1 — 2..4 — 5..0] */
export type PluralForms = readonly [one: string, few: string, many: string];

/** Возвращает форму слова без числа: pluralize(2, DAYS) → "дня" */
export const pluralize = (count: number, forms: PluralForms): string => {
  const lastDigit = Math.abs(count) % 10;
  const lastTwoDigits = Math.abs(count) % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return forms[2];
  if (lastDigit === 1) return forms[0];
  if (lastDigit >= 2 && lastDigit <= 4) return forms[1];
  return forms[2];
};

/** То же, но с числом: pluralizeWithCount(2, DAYS) → "2 дня" */
export const pluralizeWithCount = (count: number, forms: PluralForms): string =>
  `${count} ${pluralize(count, forms)}`;

// Именительный / винительный неодушевлённый
export const DAYS: PluralForms = ["день", "дня", "дней"];
export const ORDERS: PluralForms = ["заказ", "заказа", "заказов"];
export const FRIENDS: PluralForms = ["друг", "друга", "друзей"];
export const RUBLES: PluralForms = ["рубль", "рубля", "рублей"];
// Винительный одушевлённый: «пригласи 1 друга / 2 друга / 5 друзей».
// Для 2..4 берём «друга» — с цифрой это звучит естественнее строгой
// нормы («2 друзей») и совпадает с разговорным тоном приложения.
export const FRIENDS_ACC: PluralForms = ["друга", "друга", "друзей"];
// Родительный после «из N»: «из 1 друга / из 2 друзей / из 8 друзей»
export const FRIENDS_GEN: PluralForms = ["друга", "друзей", "друзей"];
// «Собери 1 такого друга / 2 таких друга / 5 таких друзей»
export const SUCH_FRIENDS: PluralForms = [
  "такого друга",
  "таких друга",
  "таких друзей",
];
// «Собери 1 засчитанного друга / 2 засчитанных друга / 5 засчитанных друзей»
export const COUNTED_FRIENDS: PluralForms = [
  "засчитанного друга",
  "засчитанных друга",
  "засчитанных друзей",
];
// «1 новый друг / 2 новых друга / 5 новых друзей»
export const NEW_FRIENDS: PluralForms = [
  "новый друг",
  "новых друга",
  "новых друзей",
];
// Согласование глагола: «остался 1 друг / осталось 2 друга»
export const REMAINS: PluralForms = ["остался", "осталось", "осталось"];
// «в 2 раза / в 5 раз больше»
export const TIMES: PluralForms = ["раз", "раза", "раз"];
// Творительный: «с 1 заказом / 2 заказами»
export const ORDERS_INS: PluralForms = ["заказом", "заказами", "заказами"];
// Дательный после «по»: «по 1 заказу / 2 заказа»
export const ORDERS_DAT: PluralForms = ["заказу", "заказа", "заказов"];

export const capitalizeFirst = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const declinateOrders = (count: number): string =>
  pluralizeWithCount(count, ORDERS);

export const getDaysText = (days: number): string =>
  pluralizeWithCount(days, DAYS);

export const calculateDaysLeft = (date: string | null | undefined) => {
  if (!date) return 0;

  const endDate = new Date(date);
  const today = new Date();

  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

export const activeReferalProgramsThisMonth = (
  referalPrograms: ConditionI[]
) => {
  return referalPrograms?.filter((item) => {
    const today = new Date();
    const startDate = new Date(item.msk_start_date);
    const endDate = new Date(item.msk_end_date);

    return (
      (startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() === today.getMonth()) ||
      (endDate.getFullYear() === today.getFullYear() &&
        endDate.getMonth() === today.getMonth())
    );
  });
};

export const activeReferalProgramsThisDay = (referalPrograms: ConditionI[]) => {
  return referalPrograms?.filter((item) => {
    const today = new Date();
    const startDate = new Date(item.msk_start_date);
    const endDate = new Date(item.msk_end_date);

    return today >= startDate && today <= endDate;
  });
};

export const maxAmountRefProgramByCategory = (
  referalPrograms: ConditionI[],
  category: ServiceType | null,
  rewardMultiplier: number = 1
) => {
  const amountWithMultiplier = (item: ConditionI) =>
    item.excluded_from_multiplier
      ? item.total_step_1_amount
      : item.total_step_1_amount * rewardMultiplier;

  if (!category) return 2000 * rewardMultiplier;

  let referalProgramsByCategory;

  if (category === "delivery") {
    referalProgramsByCategory = referalPrograms.filter(
      (item) =>
        item.tariff === "Авто" ||
        item.tariff === "Грузовой" ||
        item.tariff === "Пеший" ||
        item.tariff === "Мото"
    );
  } else {
    referalProgramsByCategory = referalPrograms.filter(
      (item) => item.tariff === serviceTypeLabel(category)
    );
  }

  if (!referalProgramsByCategory || referalProgramsByCategory.length === 0) {
    return 2000 * rewardMultiplier;
  }

  return Math.max(...referalProgramsByCategory.map(amountWithMultiplier));
};

export const formatRubles = (value: string): string =>
  `${Math.round(Number(value)).toLocaleString("ru-RU")} ₽`;
